import { GetterTree } from "vuex";
import { RootState } from "../store";
import { MarkerFeature } from "../../../../utils/models/MarkerFeature";
import { plainToClass } from "class-transformer";
import { LineStringFeature } from "../../../../utils/models/LineStringFeature";
import { Route } from "../../../../utils/models/Route";
import {
  length as turfLength,
  distance as turfDistance,
  bbox as turfBbox,
} from "@turf/turf";
import { MeasurementPrefUtility } from "../../../../utils/helpers/MeasurementPref";
import { HikeTimeUtility, PrettyTime } from "../../../../utils/helpers/HikeTime";
import { PlanTrailState } from "./PlanTrailModule";
import moment from 'moment';
import 'moment-timezone';
import {HiikerMapStyle, SavedCenter} from "../../../../utils/models/MapStyles";
import {RevenueCatWrapper} from "../../../../utils/RevenueCatWrapper";

// Module-level cache for routesWithStats (outside Vuex state to avoid getter mutation anti-pattern)
let _routeStatsCache: { version: number; data: any[] } | null = null;

const getters: GetterTree<PlanTrailState, RootState> = {
  // Legacy computed properties for backward compatibility
  activeRoute(state): Route | null {
    return state.activeRouteKey && state.routes[state.activeRouteKey]
      ? state.routes[state.activeRouteKey]
      : null;
  },

  // Convert routes object to array for components
  routesArray(state): Route[] {
    return Object.keys(state.routes)
      .map(key => {
        const route = state.routes[key];
        return {
          id: route.id,
          name: route.name,
          position: route.position,
          markers: route.markers,
          lineStrings: route.lineStrings,
          created_at: route.created_at,
          updated_at: route.updated_at,
          route_style: route.route_style,
          day: route.day,
          undoMarkers: route.undoMarkers,
          redoMarkers: route.redoMarkers,
          undoLineStrings: route.undoLineStrings,
          redoLineStrings: route.redoLineStrings,
          // Include reference fields - always include plannedTrailId if present
          plannedTrailId: route.plannedTrailId,
          isReference: route.isReference,
          referencedHikes: route.referencedHikes
        };
      })
      .sort((a, b) => a.position - b.position);
  },

  activeRouteId(state): string | null {
    return state.activeRouteKey && state.routes[state.activeRouteKey]
      ? state.routes[state.activeRouteKey].id
      : null;
  },

  getLocalizedPlannedToHikeOn(state) {
    const localDateTime = moment.utc(state.plannedToHikeOn).local();
    const localizedDateTime = localDateTime.format('YYYY-MM-DDTHH:mm');
    return localizedDateTime;
  },
  getLastUndoMarkers(state, getters) {
    return state.undoMarkers[state.undoMarkers.length - 1]
  },
  getLastRedoMarkers(state, getters) {
    return state.redoMarkers[state.redoMarkers.length - 1]
  },
  getLastUndoLineStrings(state, getters) {
    return state.undoLineStrings[state.undoLineStrings.length - 1]
  },
  getLastRedoLineStrings(state, getters) {
    return state.redoLineStrings[state.redoLineStrings.length - 1]
  },
  markersFeatureCollection(state, getters) {
    const activeRoute = getters.activeRoute;
    if (!activeRoute) {
      return {
        type: 'FeatureCollection',
        features: []
      };
    }
    return {
      type: 'FeatureCollection',
      features: activeRoute.markers.map(marker => {
        // @ts-ignore
        const markerFeature: MarkerFeature = plainToClass(MarkerFeature, marker)
        return markerFeature.toGeoJson()
      })
    }
  },
  async initialMapSettings(state): Promise<mapboxgl.MapboxOptions> {
    const isPro = await RevenueCatWrapper.isProOrProPlus()
    const style = HiikerMapStyle.getCurrentStyle(isPro).styleUrl;
    const currentCenter = SavedCenter.getCurrentCenter()
    return {
      container: 'map',
      center: currentCenter.lngLat(),
      maxPitch: 85,
      zoom: currentCenter.zoom,
      style: style
    }
  },
  trailDistanceString(state, getters) {
    let distance = turfLength(getters.lineStringFeatureCollection, { units: 'meters' })
    return MeasurementPrefUtility.preference().smallUnitFormatter(distance, true)
  },
  elevationGainString(state, getters) {
    let elevationGainTotal = 0;

    const activeRoute = getters.activeRoute;
    if (!activeRoute) return '0m';

    activeRoute.lineStrings.forEach((lineString) => {
      const lineStringFeature = plainToClass(LineStringFeature, lineString);
      const coordinates = lineStringFeature.lineStringGeometry.coordinates; // Assuming this contains altitude data

      if (coordinates.length < 2) {
        return;
      }

      let totalElevationGain = 0;
      let baseElevation = coordinates[0][2];

      for (let i = 1; i < coordinates.length; i++) {
        const elevationChange = coordinates[i][2] - baseElevation;

        if (elevationChange > 6) {
          totalElevationGain += elevationChange;
          baseElevation = coordinates[i][2]; // Reset baseElevation on significant gain
        } else if (coordinates[i][2] < baseElevation) {
          baseElevation = coordinates[i][2]; // Reset baseElevation on any descent
        }
      }

      elevationGainTotal += totalElevationGain;
    });

    return MeasurementPrefUtility.preference().smallUnitFormatter(elevationGainTotal, false);
  },
  lineStringFeatureCollection(state, getters) {
    const activeRoute = getters.activeRoute;
    if (!activeRoute) {
      return {
        type: 'FeatureCollection',
        features: []
      };
    }
    return {
      type: 'FeatureCollection',
      features: activeRoute.lineStrings.map(lineString => {
        const lineStringFeature: LineStringFeature = plainToClass(LineStringFeature, lineString)
        return lineStringFeature.toGeoJson()
      })
    }
  },
  // Legacy getter - removed. Use activeRoute.markers instead.
  // Legacy getter - removed. Use activeRoute.lineStrings instead.
  createChartData(state, getters) {
    const activeRoute = getters.activeRoute;
    if (!activeRoute) return null;

    // Inline Haversine distance (meters) — avoids turfDistance library overhead
    const R = 6371000;
    const haversineMeters = (c1: number[], c2: number[]): number => {
      const toRad = Math.PI / 180;
      const dLat = (c2[1] - c1[1]) * toRad;
      const dLon = (c2[0] - c1[0]) * toRad;
      const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(c1[1] * toRad) * Math.cos(c2[1] * toRad) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
      return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    };

    // Surface type number-to-string map
    const surfaceMap: Record<number, string> = {
      0: 'unknown', 1: 'paved', 2: 'unpaved', 3: 'asphalt', 4: 'concrete',
      5: 'cobblestone', 6: 'metal', 7: 'wood', 8: 'compactedGravel', 9: 'fineGravel',
      10: 'gravel', 11: 'dirt', 12: 'ground', 13: 'ice', 14: 'pavingStones',
      15: 'sand', 16: 'woodchips', 17: 'grass', 18: 'grassPaver'
    };

    const surfaceColorMap: Record<string, string> = {
      'unknown': '#CCCCCC', 'missing': '#CCCCCC', 'paved': '#888888',
      'unpaved': '#CD853F', 'asphalt': '#666666', 'concrete': '#BEBEBE',
      'cobblestone': '#D2691E', 'metal': '#B0C4DE', 'wood': '#DEB887',
      'compactedGravel': '#A9A9A9', 'fineGravel': '#C0C0C0', 'gravel': '#A9A9A9',
      'dirt': '#8B4513', 'ground': '#D2691E', 'ice': '#87CEEB',
      'pavingStones': '#999999', 'sand': '#F4A460', 'woodchips': '#DEB887',
      'grass': '#32CD32', 'grassPaver': '#9ACD32',
    };

    // Step 1: Count total coordinates to determine sample rate
    let totalCoords = 0;
    for (const ls of activeRoute.lineStrings) {
      totalCoords += ls.lineStringGeometry.coordinates.length;
    }

    const maxPoints = 1000;
    const step = totalCoords > maxPoints ? Math.ceil(totalCoords / maxPoints) : 1;

    // Step 2: Single pass — sample coordinates, compute distances, lookup surfaces
    const measurementUtils = MeasurementPrefUtility.preference();
    const chartdata: any = {
      labels: [], xLabels: [], distances: [], lat: [], lon: [], surfaces: [],
      coordToLineString: [],
      datasets: [{
        borderColor: '#ffffff', borderWidth: 3, fill: true,
        backgroundColor: 'rgba(255, 255, 255, 0.1)', data: [],
        segment: {
          borderColor: (ctx: any) => {
            const surfaces = ctx.chart.data.surfaces;
            if (!surfaces || surfaces.length === 0) return '#ffffff';
            return surfaceColorMap[surfaces[ctx.p0DataIndex]] || '#ffffff';
          }
        }
      }],
    };

    let globalIdx = 0;
    let cumulativeDistance = 0;
    let prevCoord: number[] | null = null;
    let sampleCount = 0;

    for (let lsIndex = 0; lsIndex < activeRoute.lineStrings.length; lsIndex++) {
      const ls = activeRoute.lineStrings[lsIndex];
      const coords = ls.lineStringGeometry.coordinates;
      const surfaceValues = ls.lineStringProperties?.extras?.surface?.values;

      // Pre-index surface segments for this linestring (avoids linear scan per point)
      let surfaceIdx = 0;

      for (let cIndex = 0; cIndex < coords.length; cIndex++) {
        const coord = coords[cIndex];

        // Accumulate distance for every coordinate (needed for accuracy)
        if (prevCoord) {
          cumulativeDistance += haversineMeters(prevCoord, coord);
        }
        prevCoord = coord;

        // Only emit sampled points
        if (sampleCount === 0) {
          // X is the real cumulative distance (in the user's big unit), so the
          // chart's linear x-axis spaces points by distance, not by array index.
          // Index spacing on a category axis is what produced the "stepping"
          // artifact on tracks with long straight segments (few, far-apart points).
          const distanceBigUnit = measurementUtils.bigUnitValue(cumulativeDistance / 1000);
          chartdata.xLabels.push(measurementUtils.bigUnitFormatter(cumulativeDistance / 1000, false));
          chartdata.distances.push(cumulativeDistance);
          chartdata.labels.push(measurementUtils.smallUnitFormatter(coord[2]));
          chartdata.lat.push(coord[1]);
          chartdata.lon.push(coord[0]);
          chartdata.datasets[0].data.push({ x: distanceBigUnit, y: measurementUtils.smallUnitValue(coord[2]) });
          chartdata.coordToLineString.push({ lineStringIndex: lsIndex, coordIndex: cIndex });

          // Surface lookup — advance index pointer (sorted segments)
          let surfaceType = 'unknown';
          if (surfaceValues) {
            while (surfaceIdx < surfaceValues.length) {
              const [startIdx, endIdx, sType] = surfaceValues[surfaceIdx];
              if (cIndex < endIdx) {
                if (cIndex >= startIdx) {
                  surfaceType = typeof sType === 'string' ? sType : (surfaceMap[sType] || 'unknown');
                }
                break;
              }
              surfaceIdx++;
            }
          }
          chartdata.surfaces.push(surfaceType);
        }

        sampleCount = (sampleCount + 1) % step;
        globalIdx++;
      }
    }

    return chartdata;
  },
  // Legacy getter - removed. Use activeRoute.markers[activeRoute.markers.length - 1] instead.
  // Legacy getter - removed. Use activeRoute.markers.length === 0 instead.
  getPtv2ImageAttributes(state) {
    const activePhotoIds = state.photosIds.filter(
      (id: string) => !state.photosMarkedForRemoval.includes(id)
    );

    return state.photosIds.map((photoId: string) => {
      const attributes: any = {
        parentClientId: state.clientId,
        clientId: photoId,
        imageableType: 'Hike',
        isCover: activePhotoIds.indexOf(photoId) === 0,
      };

      if (state.photoExistingIds[photoId]) {
        attributes.id = state.photoExistingIds[photoId];
      }

      if (state.photoSignedIds[photoId]) {
        attributes.signedId = state.photoSignedIds[photoId];
      }

      if (state.photosMarkedForRemoval.includes(photoId)) {
        attributes._destroy = true;
      }

      return attributes;
    });
  },

  existingHikeImages(state) {
    return state.existingHikeImages;
  },

  // Route management getters (using new structure)

  routesWithStats(state, getters): Array<Route & {distance: number, ascent: number, duration: string, durationSeconds: number}> {
    // Return cached result if route geometry hasn't changed
    if (_routeStatsCache && _routeStatsCache.version === state._statsVersion) {
      return _routeStatsCache.data;
    }

    const result = getters.routesArray.map((route: Route) => {
      // Calculate distance
      const routeLineStrings = {
        type: 'FeatureCollection',
        features: route.lineStrings.map(lineString => {
          const lineStringFeature: LineStringFeature = plainToClass(LineStringFeature, lineString);
          return lineStringFeature.toGeoJson();
        })
      };
      const distance = turfLength(routeLineStrings, { units: 'meters' });

      // For transport routes, skip elevation gain and duration calculations
      if (route.route_style === 'transport') {
        return {
          ...route,
          distance,
          ascent: 0,
          duration: '',
          durationSeconds: 0
        };
      }

      // Calculate elevation gain for non-transport routes
      let elevationGain = 0;
      route.lineStrings.forEach((lineString) => {
        const lineStringFeature = plainToClass(LineStringFeature, lineString);
        const coordinates = lineStringFeature.lineStringGeometry.coordinates;

        if (coordinates.length < 2) return;

        let totalElevationGain = 0;
        let baseElevation = coordinates[0][2];

        for (let i = 1; i < coordinates.length; i++) {
          const elevationChange = coordinates[i][2] - baseElevation;

          if (elevationChange > 6) {
            totalElevationGain += elevationChange;
            baseElevation = coordinates[i][2];
          } else if (coordinates[i][2] < baseElevation) {
            baseElevation = coordinates[i][2];
          }
        }

        elevationGain += totalElevationGain;
      });

      // Calculate duration using HikeTimeUtility
      const durationSeconds = HikeTimeUtility.preference().secondsDuration(elevationGain, distance);
      const duration = new PrettyTime(durationSeconds).prettyTime();

      return {
        ...route,
        distance,
        ascent: elevationGain,
        duration,
        durationSeconds,
        day: route.day
      };
    });

    // Cache the result (module-level, not in Vuex state)
    _routeStatsCache = { version: state._statsVersion, data: result };
    return result;
  },

  totalDistance(state, getters): number {
    return getters.routesWithStats.reduce((total: number, route: any) => total + route.distance, 0);
  },

  totalDistanceExcludingTransport(state, getters): number {
    return getters.routesWithStats.reduce((total: number, route: any) => {
      // Only add distance if it's a main route (exclude transport, alt, and spur)
      if (route.route_style !== 'transport' && route.route_style !== 'alt' && route.route_style !== 'spur') {
        return total + route.distance;
      }
      return total;
    }, 0);
  },

  hasTransportRoutes(state, getters): boolean {
    return getters.routesArray.some((route: Route) => route.route_style === 'transport');
  },

  isTransportOnlyPlan(state, getters): boolean {
    const routes: Route[] = getters.routesArray;
    return routes.length > 0 && routes.every((route: Route) => route.route_style === 'transport');
  },

  // Check if the plan has at least one main route (route style = 'route')
  hasMainRoute(state, getters): boolean {
    const routes: Route[] = getters.routesArray;
    // Default route_style to 'route' if undefined for backwards compatibility
    return routes.some((route: Route) => (route.route_style || 'route') === 'route');
  },

  // Validate that each hike day has at least one main route
  validateHikeDaysHaveMainRoute(state, getters): { valid: boolean; invalidDays: number[] } {
    const routesByDay = getters.routesByDay;
    const invalidDays: number[] = [];

    // Check each day
    Object.keys(routesByDay).forEach(dayKey => {
      const dayNumber = parseInt(dayKey);
      const dayRoutes = routesByDay[dayNumber];

      // Check if this day has at least one 'route' or 'transport' style route
      // Default route_style to 'route' if undefined for backwards compatibility
      const hasMainRoute = dayRoutes.some((route: any) => {
        const style = route.route_style || 'route';
        return style === 'route' || style === 'transport';
      });

      if (!hasMainRoute) {
        invalidDays.push(dayNumber);
      }
    });

    return {
      valid: invalidDays.length === 0,
      invalidDays
    };
  },

  totalAscent(state, getters): number {
    return getters.routesWithStats.reduce((total: number, route: any) => {
      // Only add ascent if it's a main route (exclude transport, alt, and spur)
      if (route.route_style !== 'transport' && route.route_style !== 'alt' && route.route_style !== 'spur') {
        return total + route.ascent;
      }
      return total;
    }, 0);
  },

  totalElevationLoss(state, getters): number {
    let totalLoss = 0;

    getters.routesArray.forEach((route: Route) => {
      // Skip transport routes for elevation calculations
      if (route.route_style === 'transport') return;

      route.lineStrings.forEach((lineString) => {
        const lineStringFeature = plainToClass(LineStringFeature, lineString);
        const coordinates = lineStringFeature.lineStringGeometry.coordinates;

        if (coordinates.length < 2) return;

        for (let i = 1; i < coordinates.length; i++) {
          const elevationChange = coordinates[i][2] - coordinates[i - 1][2];

          // Count negative elevation changes as loss
          if (elevationChange < -6) { // Using same 6m threshold as gain
            totalLoss += Math.abs(elevationChange);
          }
        }
      });
    });

    return totalLoss;
  },

  // Total estimated hike time across all main routes (excludes transport, alt,
  // and spur to match totalDistance/totalAscent). Sum the raw seconds and format
  // once so minutes aren't lost the way per-route string parsing would.
  totalDurationSeconds(state, getters): number {
    return getters.routesWithStats.reduce((total: number, route: any) => {
      if (route.route_style !== 'transport' && route.route_style !== 'alt' && route.route_style !== 'spur') {
        return total + (route.durationSeconds || 0);
      }
      return total;
    }, 0);
  },

  totalDuration(state, getters): string {
    return new PrettyTime(getters.totalDurationSeconds).prettyTime();
  },

  formattedRoutesForComponent(state, getters): Array<{id: string, name: string, distance: number, ascent: number, duration: string, route_style?: string, day?: number, isReference?: boolean, plannedTrailId?: number, referencedHikes?: any[]}> {
    return getters.routesWithStats.map((route: any) => ({
      id: route.id,
      name: route.name,
      distance: route.distance,
      ascent: route.ascent,
      duration: route.duration,
      route_style: route.route_style,
      day: route.day,
      isReference: route.isReference,
      plannedTrailId: route.plannedTrailId,
      referencedHikes: route.referencedHikes
    }));
  },

  // Group routes by day
  routesByDay(state, getters): { [day: number]: Array<any> } {
    const routes = getters.formattedRoutesForComponent;
    const routesByDay: { [day: number]: Array<any> } = {};

    routes.forEach(route => {
      if (route.day !== undefined) {
        if (!routesByDay[route.day]) {
          routesByDay[route.day] = [];
        }
        routesByDay[route.day].push(route);
      }
    });

    return routesByDay;
  },

  // Get max day number
  maxDay(state, getters): number {
    const routes = getters.routesArray;
    const days = routes.filter(r => r.day !== undefined).map(r => r.day);
    return days.length > 0 ? Math.max(...days) : 0;
  },

  // Check if we should show days
  shouldShowDays(state, getters): boolean {
    const routes = getters.routesArray;
    return routes.length > 1;
  },

  // Get planned trails attributes for hike creation
  getPlannedTrailsAttributesForHike(state, getters): Array<any> {
    const routesArray = getters.routesArray;
    // Filter out incomplete routes (routes with less than 2 markers can't form a valid route)
    const validRoutes = routesArray.filter((route: Route) => route.markers.length >= 2);
    return validRoutes.map((route: Route, index: number) => {
      // Convert route markers and linestrings to attributes
      const markerAttributes = route.markers.map(marker => {
        let markerFeature = plainToClass(MarkerFeature, marker)
        return {
          elevationMeters: Math.round(markerFeature.markerProperties.ele),
          lonlat: `POINT(${markerFeature.markerGeometry.coordinates[0]} ${markerFeature.markerGeometry.coordinates[1]})`,
          snap: markerFeature.markerProperties.isSnap
        }
      });

      const lineStringAttributes = route.lineStrings.map(lineString => {
        let lineStringFeature = plainToClass(LineStringFeature, lineString)
        const coordinates = lineStringFeature.lineStringGeometry.coordinates;

        // Defensive: skip invalid linestrings to avoid backend geometry errors
        if (!coordinates || coordinates.length < 2) {
          console.warn('[SAVE] Skipping linestring with less than 2 coordinates', {
            routeId: route.id,
            lineStringId: lineStringFeature.lineStringProperties.id
          });
          return null;
        }

        // Extract surfaces data
        const surfaceValues = lineStringFeature.lineStringProperties.extras.surface.values;
        const coordinatesCount = coordinates.length;

        // Convert surface segments to array where each element represents surface for each coordinate
        const surfaces: number[] = new Array(coordinatesCount).fill(0); // Default to 0 (unknown)

        // Process each surface segment
        surfaceValues.forEach(segment => {
          const [startIdx, endIdx, surfaceType] = segment;
          // Map surface type string to integer if needed
          let surfaceInt: number;
          if (typeof surfaceType === 'string') {
            // Map string surface types to integers based on LineStringPropertiesSurface.surfaceIntToString
            const surfaceMap: { [key: string]: number } = {
              'unknown': 0,
              'paved': 1,
              'unpaved': 2,
              'asphalt': 3,
              'concrete': 4,
              'cobblestone': 5,
              'metal': 6,
              'wood': 7,
              'compactedGravel': 8,
              'fineGravel': 9,
              'gravel': 10,
              'dirt': 11,
              'ground': 12,
              'ice': 13,
              'pavingStones': 14,
              'sand': 15,
              'woodchips': 16,
              'grass': 17,
              'grassPaver': 18,
              'missing': 0 // Treat missing as unknown
            };
            surfaceInt = surfaceMap[surfaceType] ?? 0;
          } else {
            surfaceInt = surfaceType as number;
          }

          // Fill the surface array for this segment
          for (let i = startIdx; i < endIdx && i < coordinatesCount; i++) {
            surfaces[i] = surfaceInt;
          }
        });

        const distances: number[] = [];

        if (coordinates.length > 0) {
          distances.push(0);

          for (let i = 1; i < coordinates.length; i++) {
            const previous = coordinates[i - 1];
            const current = coordinates[i];
            const segmentDistance = turfDistance(
              [previous[0], previous[1]],
              [current[0], current[1]],
              { units: 'meters' }
            );

            const cumulative = distances[i - 1] + segmentDistance;
            distances.push(Math.round(cumulative * 10) / 10);
          }
        }

        return {
          geometry: lineStringFeature.toWKT(distances),
          surfaces: surfaces,
          distances: distances
        }
      }).filter((ls): ls is any => !!ls);

      const attributes: any = {
        title: route.name,
        markersAttributes: markerAttributes,
        lineStringsAttributes: lineStringAttributes,
        position: index + 1,
        public: state.public, // Use the same privacy setting as the hiking trail
        routeStyle: route.route_style, // Include route style
        day: route.day // Include day for multi-day hikes
      };

      // If this is a referenced route, include the ptv2PlannedTrailId
      // This will update the existing PlannedTrail and associate it with this hike
      // Note: Updates to the PlannedTrail will affect all hikes that reference it
      if (route.plannedTrailId && route.isReference) {
        attributes.ptv2PlannedTrailId = String(route.plannedTrailId);
      }

      return attributes;
    });
  },

  getHikeDaysAttributes(state, getters): Array<any> {
    // Create a hike_day for each day that has at least one route.
    // Merge in optional name/description from state.dayInfo when present.
    const routesByDay = getters.routesByDay || {};
    const usedDays: number[] = Object.keys(routesByDay)
      .map(d => parseInt(d))
      .filter(d => routesByDay[d] && routesByDay[d].length > 0)
      .sort((a, b) => a - b);

    const hikeDaysArray: any[] = [];

    usedDays.forEach(dayNumber => {
      const info = state.dayInfo?.[dayNumber] || {};
      const dayAttribute: any = { dayNumber };
      if (info.id) dayAttribute.id = info.id; // include id on updates
      if (info.name) dayAttribute.name = info.name;
      if (info.description) dayAttribute.description = info.description;
      hikeDaysArray.push(dayAttribute);
    });

    return hikeDaysArray;
  },

  // Calculate difficulty based on elevation gain per km
  calculatedDifficulty(state, getters): string {
    const distanceKm = getters.totalDistanceExcludingTransport / 1000;
    if (distanceKm === 0) return 'Easy';

    const elevationGainPerKm = getters.totalAscent / distanceKm;

    if (elevationGainPerKm < 20) return 'Easy';
    if (elevationGainPerKm < 60) return 'Medium';
    if (elevationGainPerKm < 101) return 'Difficult';
    return 'Extra Difficult';
  },

  // Get the final difficulty (override or calculated)
  difficulty(state, getters): string {
    return state.difficultyOverride || getters.calculatedDifficulty;
  },

  // Get bounds of the currently active route, or earth bounds if empty
  activeRouteBounds(state, getters): [[number, number], [number, number]] {
    const activeRoute = getters.activeRoute;

    // Return earth bounds if no active route
    if (!activeRoute) {
      // Earth bounds: [minLng, minLat], [maxLng, maxLat]
      return [[-180, -85], [180, 85]];
    }

    // Check if route has any content (markers or lineStrings)
    if (activeRoute.markers.length === 0 && activeRoute.lineStrings.length === 0) {
      return [[-180, -85], [180, 85]];
    }

    // Combine both markers and lineStrings into a feature collection
    const features = [];

    // Add lineStrings if any
    if (activeRoute.lineStrings.length > 0) {
      const lineStringFeatures = activeRoute.lineStrings.map(lineString => {
        const lineStringFeature: LineStringFeature = plainToClass(LineStringFeature, lineString);
        return lineStringFeature.toGeoJson();
      });
      features.push(...lineStringFeatures);
    }

    // Add markers if any
    if (activeRoute.markers.length > 0) {
      const markerFeatures = activeRoute.markers.map(marker => {
        const markerFeature: MarkerFeature = plainToClass(MarkerFeature, marker);
        return markerFeature.toGeoJson();
      });
      features.push(...markerFeatures);
    }

    // If we have features, calculate bounds
    if (features.length > 0) {
      const featureCollection = {
        type: 'FeatureCollection',
        features: features
      };

      const bounds = turfBbox(featureCollection);

      // Return in Mapbox format: [sw, ne]
      const mapboxBounds: [[number, number], [number, number]] = [[bounds[0], bounds[1]], [bounds[2], bounds[3]]];
      return mapboxBounds;
    }

    // Fallback to earth bounds
    return [[-180, -85], [180, 85]];
  },
  
  // Get hike highlights formatted for GraphQL
  getHikeHighlightsAttributes(state) {
    return state.hikeHighlights.map(highlight => {
      const attributes: any = {
        type: highlight.type,
        title: highlight.title,
        description: highlight.description,
        lonlat: `POINT(${highlight.coordinates[0]} ${highlight.coordinates[1]})`,
        dayNumber: highlight.dayNumber,
        clientId: highlight.clientId,
        notificationDistance: highlight.notificationDistance ?? null
      };
      
      // Add images_attributes if there are signed IDs
      const imagesAttributes: any[] = [];

      if (highlight.images && highlight.images.length > 0) {
        highlight.images.forEach((image: any, index: number) => {
          if (!image) return;
          const clientId = image.clientId || image.client_id || `${highlight.clientId}_existing_${index}`;
          const attr: any = {
            clientId
          };

          if (image.id) {
            attr.id = image.id;
          }

          if (image._destroy) {
            attr._destroy = true;
          }

          imagesAttributes.push(attr);
        });
      }

      if (highlight.imageSignedIds && highlight.imageSignedIds.length > 0) {
        highlight.imageSignedIds
          .forEach((upload: any, index: number) => {
            if (!upload) return;

            if (typeof upload === 'string') {
              imagesAttributes.push({
                signedId: upload,
                clientId: `${highlight.clientId}_image_${index}`
              });
              return;
            }

            const signedId = upload.signedId || upload.signed_id;
            if (!signedId) {
              return;
            }

            const clientId = upload.clientId || upload.client_id || `${highlight.clientId}_image_${index}`;

            imagesAttributes.push({
              signedId,
              clientId
            });
          });
      }

      if (imagesAttributes.length > 0) {
        attributes.imagesAttributes = imagesAttributes;
      }

      // Build media_attachments_attributes (audio etc). mediaType is required on
      // every entry by the backend input. Removal is by omission (replace-state),
      // so existing media flagged _destroy are simply left out of the list.
      const mediaAttachmentsAttributes: any[] = [];

      if (highlight.media && highlight.media.length > 0) {
        highlight.media.forEach((item: any, index: number) => {
          if (!item || item._destroy) return;
          const clientId = item.clientId || item.client_id || `${highlight.clientId}_media_existing_${index}`;
          const attr: any = {
            clientId,
            mediaType: item.mediaType || item.media_type || 'audio'
          };
          if (item.id) attr.id = item.id;
          mediaAttachmentsAttributes.push(attr);
        });
      }

      if (highlight.mediaSignedIds && highlight.mediaSignedIds.length > 0) {
        highlight.mediaSignedIds.forEach((upload: any, index: number) => {
          if (!upload) return;
          const signedId = upload.signedId || upload.signed_id;
          if (!signedId) return;
          mediaAttachmentsAttributes.push({
            signedId,
            clientId: upload.clientId || upload.client_id || `${highlight.clientId}_media_${index}`,
            mediaType: upload.mediaType || upload.media_type || 'audio',
            title: upload.title
          });
        });
      }

      if (mediaAttachmentsAttributes.length > 0) {
        attributes.mediaAttachmentsAttributes = mediaAttachmentsAttributes;
      }

      return attributes;
    });
  }
}
export default getters
